/* eslint-disable react/prop-types */
import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { useState } from "react";
import useInitiateRefund from "../../hooks/update-hooks/issue-refund/initiate-refund";

const RefundsTable = ({ refunds = [], isLoading = false }) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const [initiatedRefundIds, setInitiatedRefundIds] = useState([]);
    const [refundIdInProgress, setRefundIdInProgress] = useState(null);
    const { mutate: initiateRefund, isLoading: isInitiating } = useInitiateRefund();
    // Map status → badge colors
    const getStatusColor = (status) => {
        switch (status) {
            case "SUCCESS":
            case "COMPLETED":
                return "bg-green-100 text-green-800";
            case "PENDING":
                return "bg-yellow-100 text-yellow-800";
            case "FAILED":
            case "CANCELLED":
                return "bg-red-100 text-red-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    const formatAmount = (amount) => {
        const num = parseFloat(amount || 0);
        return `₹${num.toFixed(2)}`;
    };

    if (isLoading) {
        return (
            <div className="bg-white rounded-2xl shadow-soft-xl p-6 mt-4">
                <div
                    className="flex justify-between items-center cursor-pointer hover:bg-gray-50 rounded-lg -m-2 p-2"
                    onClick={() => setIsExpanded((e) => !e)}
                >
                    <h3 className="text-lg font-semibold">Refunds</h3>
                    <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} className="text-gray-500" />
                </div>
                <p className="text-gray-500 text-center py-8">Loading refunds...</p>
            </div>
        );
    }

    if (!refunds || refunds.length === 0) {
        return (
            <div className="bg-white rounded-2xl shadow-soft-xl p-6 mt-4">
                <div
                    className="flex justify-between items-center cursor-pointer hover:bg-gray-50 rounded-lg -m-2 p-2"
                    onClick={() => setIsExpanded((e) => !e)}
                >
                    <h3 className="text-lg font-semibold">Refunds [0]</h3>
                    <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} className="text-gray-500" />
                </div>
                <p className="text-gray-500 text-center py-8">
                    No refunds found for this order
                </p>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-2xl shadow-soft-xl p-6 mt-4">
            <div
                className="flex justify-between items-center cursor-pointer hover:bg-gray-50 rounded-lg -m-2 p-2 mb-2"
                onClick={() => setIsExpanded((prev) => !prev)}
            >
                <h3 className="text-lg font-semibold">
                    Refunds [{refunds.length}]
                </h3>
                <FontAwesomeIcon
                    icon={isExpanded ? faChevronUp : faChevronDown}
                    className="text-gray-500"
                />
            </div>

            {isExpanded && (
                <div className="overflow-x-auto">
                    <table className="table-auto w-full">

                        <thead>
                            <tr className="text-left h-12 font-sans border-b-2 border-gray-100">
                                <th className="pl-3 font-sans text-left px-2">Refund ID</th>
                                <th className="pl-3 font-sans text-left px-2">Amount</th>
                                <th className="pl-3 font-sans text-left px-2">Status</th>
                                <th className="pl-3 font-sans text-left px-2">Transaction ID</th>
                                <th className="pl-3 font-sans text-left px-2">Bank ARN</th>
                                <th className="pl-3 font-sans text-left px-2">Action</th>
                            </tr>
                        </thead>
                        <tbody className="border-collapse">
                            {refunds.map((refund, index) => (
                                <tr
                                    key={refund.id || index}
                                    className="text-left border-t border-gray-100 h-14 font-sans hover:bg-gray-50"
                                >
                                    <td className="pl-3 py-4 font-sans text-left">
                                        <span className="font-semibold text-green-600 bg-gray-100 px-2 py-1 rounded">
                                            {refund.id}
                                        </span>
                                    </td>
                                    <td className="pl-3 py-4 font-sans text-left">
                                        <span className="font-semibold text-green-600">
                                            {formatAmount(refund.amount)}
                                        </span>
                                    </td>
                                    <td className="pl-3 py-4 font-sans text-left">
                                        <span
                                            className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(
                                                refund.status
                                            )}`}
                                        >
                                            {refund.status || "NA"}
                                        </span>
                                    </td>
                                    <td className="pl-3 py-4 font-sans text-left max-w-xs truncate">
                                        <span className="text-gray-700">
                                            {refund.transactionId || "—"}
                                        </span>
                                    </td>
                                    <td className="pl-3 py-4 font-sans text-left max-w-xs truncate">
                                        <span className="text-gray-700">
                                            {refund.bankArn || "—"}
                                        </span>
                                    </td>
                                    <td className="pl-3 py-4 font-sans text-left">
                                        {initiatedRefundIds.includes(refund.id) ? (
                                            <span className="text-gray-500 text-xs font-medium">Initiated</span>
                                        ) : refund.status === "INITIATED" ? (
                                            <button
                                                className="px-2 py-1 text-xs rounded bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50"
                                                onClick={() => {
                                                    setRefundIdInProgress(refund.id);
                                                    initiateRefund(refund.id, {
                                                        onSuccess: () => {
                                                            setInitiatedRefundIds((prev) => [...prev, refund.id]);
                                                        },
                                                        onSettled: () => {
                                                            setRefundIdInProgress(null);
                                                        },
                                                    });
                                                }}
                                                disabled={refundIdInProgress === refund.id || isInitiating}
                                                title="Issue this refund"
                                            >
                                                {refundIdInProgress === refund.id ? "Processing..." : "Initiate refund"}
                                            </button>
                                        ) : (
                                            <span className="text-gray-400 text-xs">—</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default RefundsTable;